// ── CovenantCalendarVisual ──
import { useState, useEffect, useMemo } from "react";
import { exportCSV, fmt, localFilter, now } from "../../../shared/format";
import { Btn, Card, ErrorMsg, Icon, Pagination, STh, SearchBar, Spinner, Td, TdNum, Th, useEscape } from "../../../shared/ui";
import { usePagination, useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

const CC_CLOSED = ["Waived", "Completed", "Canceled"];  // hidden by default
const CC_NO_STATUS = "(No status)";

export const CovenantCalendarVisual = () => {
    const { api, role, view, setModal, delRec, setConfirm } = useApp();
      const [rows, setRows] = useState([]);
      const [loading, setLoading] = useState(true);
      const [err, setErr] = useState(null);
      // Opens on the TABLE (the first tab) unless the link asked for the
      // calendar. The Grouped tab was removed; an old link asking for it lands
      // on the table.
      const [mode, setMode] = useState(() =>
        view && view.mode === "calendar" ? "calendar" : "table"); // "table" | "calendar"
      const [dateField, setDateField] = useState("duedate");
      const [q, setQ] = useState("");
      const [cat, setCat] = useState("");
      const [catOpen, setCatOpen] = useState(false);
      const [cursor, setCursor] = useState(() => { const d=new Date(); return { y:d.getFullYear(), m:d.getMonth() }; });

      const [statusSel, setStatusSel] = useState(null);
      // ── Overdue only ──
      // Opened from the Action Center's Overdue covenants card. The same rule
      // as that card and the sidebar badge (api/shared/covenantOverdue.js): due
      // date before today and not Completed, Canceled or Waived — In-Progress
      // and On Hold still count. Longest late first. Clearing the chip returns
      // the page to its usual status filter.
      const [overdueOnly, setOverdueOnly] = useState(() => !!(view && view.filter === "overdue"));
      const [statusOpen, setStatusOpen] = useState(false);
      // ── Next [x] days ──
      // Table only: entries due on or before today + x days, overdue included
      // (a late item is still due "within the next five days" — it is due now).
      // Default 5. Blank = no limit. Off when the page was opened for overdue
      // only: that view's job is every late item, however late.
      const [nextDays, setNextDays] = useState(() => (view && view.filter === "overdue" ? "" : "5"));
      useEscape(catOpen || statusOpen ? () => { setCatOpen(false); setStatusOpen(false); } : null);

      const load = () => {
        setLoading(true);
        api.get("getCovenantCalendar")
          .then(d=>{setRows(d);setLoading(false);})
          .catch(e=>{setErr(e.message);setLoading(false);});
      };
      useEffect(()=>{ load(); },[]);

      const parseLocal = (s) => { if(!s) return null; const [y,m,d]=String(s).slice(0,10).split("-").map(Number); return new Date(y,m-1,d); };
      const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
      const fmt = (s) => { const d=parseLocal(s); return d ? d.toLocaleDateString() : "—"; };
      const statusOf = (r) => (r.status && String(r.status).trim() ? r.status : CC_NO_STATUS);

      const now = new Date();
      const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const todayKey = ymd(midnight);

      // ---- status options + selection ----
      const allStatuses = useMemo(() => {
        const set = new Set();
        rows.forEach(r => set.add(statusOf(r)));
        CC_CLOSED.forEach(s => set.add(s));
        return Array.from(set).sort((a,b)=> a===CC_NO_STATUS?1 : b===CC_NO_STATUS?-1 : String(a).localeCompare(String(b)));
      }, [rows]);

      const defaultSel = useMemo(() => allStatuses.filter(s => !CC_CLOSED.includes(s)), [allStatuses]);
      const selected = statusSel ?? defaultSel;

      // ---- category options ----
      const categories = useMemo(() => [...new Set(rows.map(r=>r.covenantcategory).filter(Boolean))].sort(), [rows]);

      // ── Days delinquent ──────────────────────────────────────────────────
      //
      // BANDED ON HOW LATE IT IS, which is deliberately NOT what statusColor
      // above does. The calendar squares answer "what is coming up" — amber
      // means due in the next seven days. This column answers "how bad is it
      // now" — amber means a week or less past due, red means longer. Both are
      // right for their own view; they would be wrong swapped.
      //
      // Not-yet-due and completed rows are null, not a negative: the column
      // measures delinquency, so everything that is not delinquent is equally
      // not delinquent, shows an em dash, and sorts together at the end.
      const dlqOf = (r) => {
        if (r.completeddate) return null;               // done — not late any more
        const due = parseLocal(r.duedate);
        if (!due) return null;
        const d = Math.floor((midnight - due) / 86400000);
        return d >= 0 ? d : null;                       // 0 = due today
      };

      // THE CALENDAR'S OWN HEXES DO NOT WORK AS TEXT. #ef4444 is 3.76:1 on
      // white and #ffb20d is 1.81:1 — fine as a fill behind white text on a
      // chip, unreadable as dark-on-light in a table cell. These are the same
      // hue families taken darker: 6.57:1 and 5.93:1, both clear of 4.5.
      const DLQ_RED   = "#B42318";   // more than a week past due
      const DLQ_AMBER = "#8A5A00";   // a week or less, including due today
      const dlqColor = (d) => d == null ? null : (d > 7 ? DLQ_RED : DLQ_AMBER);

      // THE WHOLE ROW is shaded by how late it is:
      //   light red    more than 7 days delinquent — the same line the overdue
      //                alerts draw (sidebar badge, Action Center card, account
      //                flag), so a row that raised an alert looks like it here
      //   light amber  1 to 7 days delinquent — late, but not yet an alert
      // Due today (0 days) is not shaded. A slightly deeper shade on hover keeps
      // the hover visible on shaded rows.
      const ROW_RED = "#FDECEA", ROW_RED_HOVER = "#FAD9D4";
      const ROW_AMBER = "#FEF3D7", ROW_AMBER_HOVER = "#FCE7B2";
      const rowBg = (r) => (r.daysdlq == null || r.daysdlq <= 0 ? "" : r.daysdlq > 7 ? ROW_RED : ROW_AMBER);
      const rowHover = (r) => (r.daysdlq == null || r.daysdlq <= 0 ? "var(--color-background-secondary)"
                               : r.daysdlq > 7 ? ROW_RED_HOVER : ROW_AMBER_HOVER);

      // daysdlq IS DERIVED HERE, on visibleRows, so every view that reads the
      // rows (the table's cards, the CSV) gets it from one place.
      const visibleRows = useMemo(
        () => rows
          .filter(r => (overdueOnly
              ? !CC_CLOSED.includes(statusOf(r)) && (() => { const due = parseLocal(r.duedate); return !!due && due < midnight; })()
              : selected.includes(statusOf(r)))
            && (!cat || r.covenantcategory === cat))
          .map(r => ({ ...r, daysdlq: dlqOf(r) })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rows, selected, cat, overdueOnly]
      );

      const setStatuses = (arr) => { setStatusSel(arr); reset(); };
      const toggleStatus = (s) => setStatuses(selected.includes(s) ? selected.filter(x=>x!==s) : [...selected, s]);

      const statusLabel =
        selected.length === 0 ? "Status: none" :
        selected.length === allStatuses.length ? "Status: all" :
        selected.length === 1 ? `Status: ${selected[0]}` :
        `Status: ${selected.length} selected`;

      const catLabel = cat ? `Category: ${cat}` : "Category: all";

      // ---- calendar plumbing ----
      const byDay = useMemo(() => {
        const map={};
        visibleRows.forEach(r=>{ const dt=parseLocal(r[dateField]); if(!dt) return; const k=ymd(dt); (map[k]=map[k]||[]).push(r); });
        return map;
      }, [visibleRows, dateField]);

      const cells = useMemo(() => {
        const first=new Date(cursor.y, cursor.m, 1);
        const start=new Date(cursor.y, cursor.m, 1-first.getDay());
        return Array.from({length:42},(_,i)=>{ const dt=new Date(start.getFullYear(),start.getMonth(),start.getDate()+i); return { dt, key:ymd(dt), inMonth:dt.getMonth()===cursor.m }; });
      }, [cursor]);

      const statusColor = (r) => {
        if (r.completeddate) return "#22c55e";
        const due=parseLocal(r.duedate); if(!due) return "var(--color-primary, #12b5db)";
        const diff=Math.floor((due-midnight)/86400000);
        if (diff<0) return "#ef4444";
        if (diff<=7) return "var(--color-accent, #ffb20d)";
        return "var(--color-primary, #12b5db)";
      };

      const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleString("default",{ month:"long", year:"numeric" });
      const move = (delta) => setCursor(c=>{ const d=new Date(c.y, c.m+delta, 1); return { y:d.getFullYear(), m:d.getMonth() }; });
      const dow = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

      // ---- table plumbing (plain view: sortable + paginated) ----
      // daysdlq already rode in on visibleRows. It has to be a real property
      // rather than something the cell works out at render time: useSortable
      // sorts on row[sortKey] and the CSV export reads named columns off the
      // row. One derivation, four consumers.
      const daysN = /^\d+$/.test(String(nextDays).trim()) ? parseInt(nextDays, 10) : null;
      const horizon = daysN == null ? null : new Date(midnight.getFullYear(), midnight.getMonth(), midnight.getDate() + daysN);
      const inWindow = (r) => { if (horizon == null) return true; const due = parseLocal(r.duedate); return !!due && due <= horizon; };
      const filtered = localFilter(visibleRows, q, ["companyname","covenantcategory","covenantname","status","covenantfrequency","portfoliomanagername"])
        .filter(inWindow);
      const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, overdueOnly ? 'daysdlq' : 'duedate', overdueOnly ? 'desc' : 'asc');

      // ── Two cards: 3rd Party Exams on top, everything else below ──
      // One sort for both (clicking a header sorts both cards the same way);
      // each card pages on its own.
      const isExam = (r) => String(r.covenantcategory || "").trim().toLowerCase() === "3rd party exam";
      const examRows  = sorted.filter(isExam);
      const otherRows = sorted.filter(r => !isExam(r));
      const exam  = usePagination(examRows, 100);
      const other = usePagination(otherRows, 100);
      const reset = () => { exam.reset(); other.reset(); };

      if (loading) return <Spinner />;
      if (err) return <ErrorMsg msg={err} onRetry={load} />;

      const modeBtn = (
        <div key="mode" style={{ display:"flex", border:"1px solid var(--color-border, #e5e7eb)", borderRadius:6, overflow:"hidden" }}>
          {[["table","Table"],["calendar","Calendar"]].map(([m,lbl])=>(
            <button key={m} onClick={()=>setMode(m)} style={{
              padding:"6px 12px", fontSize:12, cursor:"pointer", border:"none",
              background: mode===m ? "var(--color-primary, #12b5db)" : "transparent",
              color: mode===m ? "#fff" : "var(--color-text-secondary)"
            }}>{lbl}</button>
          ))}
        </div>
      );

      const quickBtn = (label, onClick) => (
        <button onClick={onClick} style={{ flex:1, padding:"4px 8px", fontSize:12, cursor:"pointer", border:"1px solid var(--color-border, #e5e7eb)", borderRadius:4, background:"transparent", color:"var(--color-text-secondary)" }}>{label}</button>
      );

      // Category filter — same Btn+dropdown format as Status
      const categoryFilter = (
        <div key="catfilter" style={{ position:"relative" }}>
          <Btn onClick={()=>setCatOpen(o=>!o)}>{catLabel} ▾</Btn>
          {catOpen && (
            <>
              <div onClick={()=>setCatOpen(false)} style={{ position:"fixed", inset:0, zIndex:900 }} />
              <div style={{ position:"absolute", left:0, top:"calc(100% + 4px)", zIndex:901, background:"var(--color-background, #fff)", border:"1px solid var(--color-border, #e5e7eb)", borderRadius:8, boxShadow:"0 4px 16px rgba(0,0,0,0.15)", minWidth:210, maxHeight:280, overflowY:"auto", padding:8 }}>
                <label style={{ display:"flex", alignItems:"center", gap:8, padding:"4px", fontSize:13, cursor:"pointer" }}>
                  <input type="radio" name="cccat" checked={cat===""} onChange={()=>{ setCat(""); reset(); setCatOpen(false); }} />
                  <span>All Categories</span>
                </label>
                {categories.map(c=>(
                  <label key={c} style={{ display:"flex", alignItems:"center", gap:8, padding:"4px", fontSize:13, cursor:"pointer" }}>
                    <input type="radio" name="cccat" checked={cat===c} onChange={()=>{ setCat(c); reset(); setCatOpen(false); }} />
                    <span>{c}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      );

      const statusFilter = (
        <div key="statusfilter" style={{ position:"relative" }}>
          <Btn onClick={()=>setStatusOpen(o=>!o)}>{statusLabel} ▾</Btn>
          {statusOpen && (
            <>
              <div onClick={()=>setStatusOpen(false)} style={{ position:"fixed", inset:0, zIndex:900 }} />
              <div style={{ position:"absolute", left:0, top:"calc(100% + 4px)", zIndex:901, background:"var(--color-background, #fff)", border:"1px solid var(--color-border, #e5e7eb)", borderRadius:8, boxShadow:"0 4px 16px rgba(0,0,0,0.15)", minWidth:210, padding:8 }}>
                <div style={{ display:"flex", gap:6, marginBottom:6, paddingBottom:6, borderBottom:"1px solid var(--color-border, #e5e7eb)" }}>
                  {quickBtn("All",  ()=>setStatuses([...allStatuses]))}
                  {quickBtn("None", ()=>setStatuses([]))}
                  {quickBtn("Default", ()=>setStatuses([...defaultSel]))}
                </div>
                {allStatuses.map(s=>(
                  <label key={s} style={{ display:"flex", alignItems:"center", gap:8, padding:"4px", fontSize:13, cursor:"pointer" }}>
                    <input type="checkbox" checked={selected.includes(s)} onChange={()=>toggleStatus(s)} />
                    <span style={{ fontStyle: s===CC_NO_STATUS ? "italic" : "normal", color: s===CC_NO_STATUS ? "var(--color-text-secondary)" : "inherit" }}>{s}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      );

      // Everything the clamped cell cannot show: who wrote it, when, and the
      // whole text rather than the first forty characters.
      const noteTooltip = (r) => [
        r.lastnoteat ? fmt(r.lastnoteat) : null,
        r.lastnoteby || null,
      ].filter(Boolean).join(" · ") + (r.lastnoteat || r.lastnoteby ? "\n" : "") + (r.lastnote || "");

      const rowTds = (r) => (
        <>
          <Td style={{ fontWeight:500 }}>{r.companyname||"—"}</Td>
          <Td>{r.covenantcategory||"—"}</Td>
          <Td>{r.covenantname||"—"}</Td>
          <Td>{r.covenantfrequency||"—"}</Td>
          <Td>{fmt(r.testdate)}</Td>
          <Td>{fmt(r.duedate)}</Td>
          {(() => { const d = r.daysdlq;
            return <TdNum style={{ color: dlqColor(d) || "inherit", fontWeight: d == null ? 400 : 700 }}>
              {d == null ? "—" : d}
            </TdNum>; })()}
          <Td>{fmt(r.completeddate)}</Td>
          <Td>{r.status||"—"}</Td>
          {/* Opens the Box folder/file in a new tab without opening the row. */}
          <Td onClick={e=>e.stopPropagation()}>
            {r.boxurl && /^https?:\/\//i.test(String(r.boxurl).trim())
              ? <a href={String(r.boxurl).trim()} target="_blank" rel="noopener noreferrer" title={r.boxurl}
                   style={{ color:"var(--color-primary-dark, #116588)", textDecoration:"underline", fontWeight:500 }}>Box ↗</a>
              : <span style={{ color:"var(--color-text-secondary)" }}>—</span>}
          </Td>
          {/* The table is white-space:nowrap, so an unbounded note would push
              every column after it off screen. Clamped with an ellipsis; the
              full text, its author and its date are in the tooltip, and the
              row still opens the record. */}
          <Td style={{ maxWidth:320, overflow:"hidden", textOverflow:"ellipsis" }}
              title={r.lastnote ? noteTooltip(r) : undefined}>
            {r.lastnote
              ? <span style={{ color:"var(--color-text-secondary)" }}>{r.lastnote}</span>
              : "—"}
          </Td>
          <Td><div style={{ display:"flex", gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
            <Btn onClick={()=>setModal({ type:"covenantcalendar-view", rec:{...r, id:r.id} })}><Icon type="view" /></Btn>
            {role.canEdit&&<Btn onClick={()=>setModal({ type:"covenantcalendar-edit", rec:{...r, id:r.id}, onDone:load })}><Icon type="edit" /></Btn>}
            {role.canDelete&&<Btn variant="danger" onClick={()=>setConfirm({
              // NO CLAIM ABOUT WHAT ELSE GOES. Field exam exceptions are
              // believed to cascade and notes attached to this entry are
              // believed to be orphaned rather than removed — neither is
              // verified here, and a confirm dialog that states a consequence
              // it cannot guarantee is worse than one that does not mention it.
              msg:`Delete this calendar entry${r.companyname ? " for " + r.companyname : ""}?`
                  + " This cannot be undone.",
              onConfirm:async()=>{ await delRec("CovenantCalendar", r.id, "Deleted covenant calendar entry", load); setConfirm(null); }
            })}><Icon type="delete" /></Btn>}
          </div></Td>
        </>
      );

      const headerRow = (
        <thead><tr>
          <STh label="Account"    sortKey="companyname"          currentKey={sortKey} currentDir={sortDir} onSort={mode==="table"?toggle:undefined} />
          <STh label="Category"   sortKey="covenantcategory"     currentKey={sortKey} currentDir={sortDir} onSort={mode==="table"?toggle:undefined} />
          <STh label="Covenant"   sortKey="covenantname"         currentKey={sortKey} currentDir={sortDir} onSort={mode==="table"?toggle:undefined} />
          <STh label="Frequency"  sortKey="covenantfrequency"    currentKey={sortKey} currentDir={sortDir} onSort={mode==="table"?toggle:undefined} />
          <STh label="Test Date"  sortKey="testdate"             currentKey={sortKey} currentDir={sortDir} onSort={mode==="table"?toggle:undefined} />
          <STh label="Due Date"   sortKey="duedate"              currentKey={sortKey} currentDir={sortDir} onSort={mode==="table"?toggle:undefined} />
          <STh label="Days DLQ"   sortKey="daysdlq"              currentKey={sortKey} currentDir={sortDir} onSort={mode==="table"?toggle:undefined} numeric />
          <STh label="Completed"  sortKey="completeddate"        currentKey={sortKey} currentDir={sortDir} onSort={mode==="table"?toggle:undefined} />
          <STh label="Status"     sortKey="status"               currentKey={sortKey} currentDir={sortDir} onSort={mode==="table"?toggle:undefined} />
          <STh label="Box"        sortKey="boxurl"               currentKey={sortKey} currentDir={sortDir} onSort={mode==="table"?toggle:undefined} />
          {/* Sorts on the note's DATE, not its text — alphabetical notes tell
              nobody anything, "most recently noted" is a real question. */}
          <STh label="Latest Note" sortKey="lastnoteat"          currentKey={sortKey} currentDir={sortDir} onSort={mode==="table"?toggle:undefined} />
          <Th></Th>
        </tr></thead>
      );

      return <>
        {/* Toolbar — full-width row: controls left, search/export right (table) */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, marginBottom:16, flexWrap:"wrap", width:"100%" }}>
          {/* left group — everything except search/export */}
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            {modeBtn}
            {categoryFilter}
            {overdueOnly
              ? <span style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"5px 10px", borderRadius:999,
                               background:"#FBE4DD", color:"#8a2d1c", fontSize:12.5, fontWeight:600 }}>
                  Overdue only
                  <button onClick={()=>{ setOverdueOnly(false); reset(); }} title="Show every status again"
                          style={{ border:"none", background:"none", color:"inherit", cursor:"pointer", padding:0, fontSize:14, lineHeight:1 }}>×</button>
                </span>
              : statusFilter}
            {mode==="table" && (
              <span style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:13, color:"var(--color-text-secondary)" }}
                    title="Due on or before today plus this many days, overdue included. Clear it to show every due date.">
                Next
                <input type="number" min="0" value={nextDays} placeholder="all"
                       onChange={e=>{ setNextDays(e.target.value); reset(); }}
                       style={{ width:56, padding:"5px 6px", fontSize:13, border:"1px solid var(--color-border, #e5e7eb)", borderRadius:6, textAlign:"right" }} />
                days
                {nextDays !== "" && (
                  <button onClick={()=>{ setNextDays(""); reset(); }} title="Show every due date"
                          style={{ border:"none", background:"none", cursor:"pointer", color:"inherit", fontSize:14, padding:0, lineHeight:1 }}>×</button>
                )}
              </span>
            )}
            {mode==="calendar" && (
              <>
                <Btn onClick={()=>move(-1)}>‹</Btn>
                <Btn onClick={()=>setCursor(()=>{ const d=new Date(); return { y:d.getFullYear(), m:d.getMonth() }; })}>Today</Btn>
                <Btn onClick={()=>move(1)}>›</Btn>
                <span style={{ fontWeight:600, minWidth:150, textAlign:"center" }}>{monthLabel}</span>
                <select value={dateField} onChange={e=>setDateField(e.target.value)} style={{ padding:"6px 8px" }}>
                  <option value="duedate">By due date</option>
                  <option value="testdate">By test date</option>
                </select>
              </>
            )}
          </div>

          {/* right group — search + export only (table) */}
          {mode!=="calendar" && (
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <SearchBar value={q} onChange={v=>{ setQ(v); reset(); }} placeholder="Search covenant calendar..." />
              <Btn onClick={()=>exportCSV(sorted,["companyname","covenantcategory","covenantname","covenantfrequency","testdate","duedate","daysdlq","completeddate","status","boxurl","portfoliomanagername","lastnoteat","lastnoteby","lastnote"],"covenant-calendar.csv")}>Export CSV</Btn>
            </div>
          )}
        </div>

        {mode==="calendar" && (
          <Card style={{ padding:0, overflow:"hidden" }}>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", background:"var(--color-background-secondary)" }}>
              {dow.map(d=><div key={d} style={{ padding:8, fontWeight:600, fontSize:12, textAlign:"center" }}>{d}</div>)}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)" }}>
              {cells.map(c=>{
                const items=byDay[c.key]||[]; const isToday=c.key===todayKey;
                return (
                  <div key={c.key} style={{ minHeight:112, borderTop:"1px solid var(--color-border, #e5e7eb)", borderLeft:"1px solid var(--color-border, #e5e7eb)", padding:6, background:c.inMonth?"":"var(--color-background-secondary)", opacity:c.inMonth?1:0.5 }}>
                    <div style={{ fontSize:12, fontWeight:isToday?700:500, color:isToday?"var(--color-primary, #12b5db)":"inherit", marginBottom:4 }}>{c.dt.getDate()}</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                      {items.map(r=>(
                        <div key={r.id} title={`${r.companyname} — ${r.covenantname}`}
                          onClick={()=>setModal({ type:"covenantcalendar-view", rec:{ ...r, id:r.id } })}
                          style={{ cursor:"pointer", fontSize:11, padding:"2px 5px", borderRadius:4, color:"#fff", background:statusColor(r), whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                          {r.companyname}: {r.covenantname}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {mode==="table" && [
          { key:"exam",  title:"3rd Party Exams",       rows:examRows,  pg:exam },
          { key:"other", title:"All other covenants",   rows:otherRows, pg:other },
        ].map(c => (
          <Card key={c.key} style={{ overflowX:"auto", marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:8 }}>
              <span style={{ fontSize:13, fontWeight:700 }}>{c.title}</span>
              <span style={{ fontSize:12, color:"var(--color-text-secondary)" }}>
                ({c.rows.length}){horizon ? ` · due by ${horizon.toLocaleDateString()}, overdue included` : ""}
              </span>
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
              {headerRow}
              <tbody>
                {c.rows.length === 0 && (
                  <tr><td colSpan={99} style={{ textAlign:"center", color:"var(--color-text-secondary)", padding:16, fontSize:13 }}>
                    {horizon ? `Nothing due in the next ${daysN} day${daysN === 1 ? "" : "s"}.` : "No entries."}
                  </td></tr>
                )}
                {c.pg.paginated.map(r=>(
                  <tr key={r.id} style={{ cursor:"pointer", background: rowBg(r) }} onClick={()=>setModal({ type:"covenantcalendar-view", rec:{...r, id:r.id} })} onMouseEnter={e=>e.currentTarget.style.background=rowHover(r)} onMouseLeave={e=>e.currentTarget.style.background=rowBg(r)}>
                    {rowTds(r)}
                  </tr>
                ))}
              </tbody>
            </table>
            {c.pg.totalPages > 1 && <Pagination page={c.pg.page} totalPages={c.pg.totalPages} total={c.pg.total} pageSize={100} onPage={c.pg.setPage} />}
          </Card>
        ))}
      </>;
    };
